import WebSocketClient from './websocket'
import { 
  wsConnect, 
  wsDisconnect, 
  wsStatusChanged, 
  wsMessageReceived,
  wsError
} from '../actions/websocketActions'
import { finalizeAssistantMessage } from '../actions/messageActions'

class ReduxWebSocketManager {
  constructor(store) {
    this.store = store
    this.clients = new Map() // sessionId -> WebSocketClient
    this.documentCountKey = 'tevv_total_documents_count'
  }
  
  // Reset document count (useful for debugging or when needed)
  resetDocumentCount() {
    localStorage.removeItem(this.documentCountKey)
    console.log('🗺️ Document count reset')
  }
  
  // Get current persistent document count
  getPersistentDocumentCount() {
    return parseInt(localStorage.getItem(this.documentCountKey) || '0')
  }
  
  // Set persistent document count
  setPersistentDocumentCount(count) {
    localStorage.setItem(this.documentCountKey, count.toString())
  }

  connect(sessionId, wsUrl, token) {
    // Disconnect existing client for this session
    if (this.clients.has(sessionId)) {
      this.disconnect(sessionId)
    }

    const client = new WebSocketClient(
      wsUrl,
      token,
      (data) => this.handleMessage(sessionId, data),
      (error) => this.handleError(sessionId, error),
      (status) => this.handleStatusChange(sessionId, status)
    )

    this.clients.set(sessionId, client)
    client.connect()

    // Dispatch connection action
    this.store.dispatch(wsConnect(sessionId, wsUrl))

    return client
  }

  disconnect(sessionId) {
    const client = this.clients.get(sessionId)
    if (client) {
      client.disconnect()
      this.clients.delete(sessionId)
      this.store.dispatch(wsDisconnect(sessionId))
    }
  }

  disconnectAll() {
    this.clients.forEach((client, sessionId) => {
      this.disconnect(sessionId)
    })
  }

  handleMessage(sessionId, data) {
    console.log('🔄 Processing WebSocket message for session:', sessionId, 'phase:', data.phase)
    this.store.dispatch(wsMessageReceived(data))

    // Handle final answer completion
    if (data.phase === 'final_answer') {
      console.log('🏁 Final answer phase received for session:', sessionId)
      const state = this.store.getState()
      const sessionMessages = state.messages.messagesBySession[sessionId] || []
      
      console.log('📋 Session messages:', sessionMessages.length, 'messages found')
      console.log('💬 Messages in session:', sessionMessages)
      
      // Find the last assistant message that's streaming
      const assistantMessages = sessionMessages
        .map(id => state.messages.messages[id])
        .filter(msg => msg && msg.role === 'assistant' && msg.isStreaming)
      
      console.log('🤖 Assistant messages found:', assistantMessages.length)
      assistantMessages.forEach(msg => {
        console.log('  - Message:', msg.id, 'streaming:', msg.isStreaming, 'content length:', msg.content?.length || 0)
      })
      
      let lastAssistantMessage = assistantMessages.pop()
      let lastAssistantMessageId = lastAssistantMessage?.id

      // If no streaming assistant message found, create one as a fallback
      if (!lastAssistantMessageId && data.payload?.answer_md) {
        console.log('⚠️ No streaming assistant message found, creating fallback message')
        const { addMessage } = require('../actions/messageActions')
        const fallbackMessageId = `fallback-${Date.now()}`
        this.store.dispatch(addMessage({
          id: fallbackMessageId,
          sessionId: sessionId,
          role: 'assistant',
          content: '',
          status: 'receiving',
          isStreaming: true
        }))
        lastAssistantMessageId = fallbackMessageId
      }

      console.log('🎯 Target assistant message ID:', lastAssistantMessageId)
      console.log('📄 Answer content length:', data.payload?.answer_md?.length || 0)

      if (lastAssistantMessageId && data.payload?.answer_md) {
        console.log('✅ Finalizing assistant message with answer')
        this.store.dispatch(finalizeAssistantMessage(
          lastAssistantMessageId,
          data.payload.answer_md,
          data.payload.citations || []
        ))
        
        // Auto-disconnect after final answer (with delay)
        setTimeout(() => {
          this.disconnect(sessionId)
        }, 1000)
        
        // Track document count accumulation across searches
        if (data.payload?.citations) {
          const state = this.store.getState()
          const currentStreamData = state.messages.streamData
          
          // Get current persistent document count from localStorage or initial count
          let persistentCount = this.getPersistentDocumentCount()
          
          // If this is the first time or we have initial DB stats, sync with actual count
          if (persistentCount === 0 && currentStreamData.db_stats?.db_total_docs) {
            persistentCount = currentStreamData.db_stats.db_total_docs
            this.setPersistentDocumentCount(persistentCount)
          }
          
          // Count unique new documents from this search
          const uniqueNewDocuments = new Set()
          let foundDocuments = 0
          
          // Count documents from citations (with URLs)
          data.payload.citations.forEach(citation => {
            if (citation.source && citation.source !== 'Research Database' && citation.url) {
              uniqueNewDocuments.add(citation.url)
            }
          })
          
          // Get document count from summary stats if available
          if (data.payload.summary_stats?.total_documents) {
            foundDocuments = data.payload.summary_stats.total_documents
          } else if (currentStreamData.agent_progress) {
            // Fallback: count from agent progress
            Object.values(currentStreamData.agent_progress).forEach(agent => {
              if (agent.documents_found) {
                foundDocuments += agent.documents_found
              }
            })
          }
          
          // Use the higher of citations or reported documents found
          const documentsToAdd = Math.max(uniqueNewDocuments.size, foundDocuments)
          
          if (documentsToAdd > 0) {
            // Add to persistent count
            const newPersistentCount = persistentCount + documentsToAdd
            this.setPersistentDocumentCount(newPersistentCount)
            
            console.log('📊 Document count accumulation:', {
              previousPersistent: persistentCount,
              citationsFound: uniqueNewDocuments.size,
              agentDocuments: foundDocuments,
              adding: documentsToAdd,
              newPersistent: newPersistentCount,
              initialDbCount: currentStreamData.db_stats?.db_total_docs || 'unknown'
            })
            
            // Update the stream data with accumulated count
            this.store.dispatch(wsMessageReceived({
              phase: 'db_stats_update',
              payload: {
                db_stats: {
                  ...currentStreamData.db_stats,
                  db_total_docs: newPersistentCount,
                  related_found: currentStreamData.db_stats?.related_found || 0,
                  documents_added_this_search: documentsToAdd
                }
              }
            }))
          }
        }
      } else {
        console.log('❌ Cannot finalize message - missing:', {
          messageId: !!lastAssistantMessageId,
          answer: !!data.payload?.answer_md,
          payload: !!data.payload,
          sessionMessages: sessionMessages.length,
          assistantMessages: assistantMessages.length
        })
        
        // Don't disconnect if we couldn't process the final answer
        console.error('Failed to process final answer - keeping connection open')
      }
    }
  }

  handleError(sessionId, error) {
    this.store.dispatch(wsError(error))
  }

  handleStatusChange(sessionId, status) {
    this.store.dispatch(wsStatusChanged(status))
  }

  getClient(sessionId) {
    return this.clients.get(sessionId)
  }
}

export default ReduxWebSocketManager